import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { Passenger } from '../models/PassengerModel';

const qs = (filters?: Record<string, any>) => {
  if (!filters) return '';
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
};

export type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

const usePassengerService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);
  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);
  const list = useCallback(
    (filters?: Record<string, any>, events?: any, signal?: AbortSignal) => {
      return doGet({
        url: `${EndPoints.PASSENGERS.LIST}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: v => setFlag('list', v),
        signal,
        ...events,
      } as ServiceParams);
    },
    [doGet, setFlag]
  );

  const store = useCallback(
    (payload: Passenger, events?: any) =>
      doPost({ url: EndPoints.PASSENGERS.STORE, body: payload, setFetching: v => setFlag('store', v), ...events } as ServiceParams),
    [doPost, setFlag]
  );

  const update = useCallback(
    (id: number, payload: Passenger, events?: any) =>
      doPut({ url: EndPoints.PASSENGERS.UPDATE.replace(':id', String(id)), body: payload, setFetching: v => setFlag('update', v), ...events } as ServiceParams),
    [doPut, setFlag]
  );

  const updateFromService = useCallback(
    (id: number, payload: Record<string, unknown>, events?: any) =>
      doPut({
        url: EndPoints.PASSENGERS.UPDATE_FROM_SERVICE.replace(':id', String(id)),
        body: payload,
        setFetching: (v) => setFlag('updateFromService', v),
        ...events,
      } as ServiceParams),
    [doPut, setFlag]
  );

  const remove = useCallback(
    (id: number, events?: any) =>
      doDelete({ url: EndPoints.PASSENGERS.DELETE.replace(':id', String(id)), setFetching: v => setFlag('delete', v), ...events } as ServiceParams),
    [doDelete, setFlag]
  );
  const create = useCallback(
    (events: Events = {}) =>
      doGet({
        url: EndPoints.PASSENGERS.CREATE,
        setFetching: v => setFetch('create', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  
  const edit = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.PASSENGERS.EDIT.replace(':id', String(id)),
        setFetching: (v) => setFetch('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );
  const byCompany = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.PASSENGERS.GET_BY_COMPANY.replace(':id', String(id)),
        setFetching: (v) => setFetch('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );


  const listAddress = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.PASSENGERS.ADDRESS_LIST.replace(':id', String(id)),
        setFetching: (v) => setFetch('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );
  return { fetching, list, store, update, updateFromService, remove, create, edit, byCompany, listAddress };
};

export default usePassengerService;
